import ListAllItems from '@/brain/item-pricing/list-all-items'
import SelectQuoteItem from '@/brain/item-pricing/select-quote-item'
import SaveSaleMinimum from '@/brain/item-pricing/save-sale-minimum'
import UpdateSaleList from '@/brain/item-pricing/update-sale-list'
import UpdateDynamicList from '@/brain/item-pricing/update-dynamic-list'
import UpdateDynamicColumn from '@/brain/item-pricing/update-dynamic-column'
import AutoSetAssociated from '@/brain/item-pricing/auto-set-associated'
import AssociateItem from '@/brain/item-pricing/associate-item'
import ApplyMiniumRule from '@/brain/item-pricing/apply-minimum-rule'
import AddColumn from '@/brain/item-pricing/add-column'
import UpdateDynamicLabs from '@/brain/item-pricing/update-dynamic-labs'
import UpdateDynamicRandomLabs from '@/brain/item-pricing/update-dynamic-random-labs'
import UpdateDynamicGroupTitle from '@/brain/item-pricing/update-dynamic-group-title'
import DefineLowestMaxPrice from '@/brain/item-pricing/define-lowest-max-price'
import RemoveDynamicGroup from '@/brain/item-pricing/remove-dynamic-group'

const state = {
  listItems: [],
  filteredList: [],
  groupedFilter: {
    number: {},
    description: {},
    unity: {},
    costLab: {},
    maxLab: {}
  },
  dynamicColumnHeader: [],
  dynamicColumns: [],
  configPanel: {
    proposalMargin: 20,
    decimalPrecision: 4
  }
}

const getters = {
  GET_ALL_LIST: (state) => state.listItems,
  FILTERED_LIST: (state) => state.filteredList,
  CONFIG_PANEL: (state) => state.configPanel,
  PROPOSAL_MARGIN: (state) => state.configPanel.proposalMargin,
  DECIMAL_PRECISION: (state) => state.configPanel.decimalPrecision,
  GROUPED_FILTER_BY_FIELD: state => fieldName => state.groupedFilter[fieldName],
  GET_ITEM_BY_INDEX: state => itemIdx => state.listItems[itemIdx],
  GET_ITEM_BY_QUOTE_ID: state => quoteId => state.listItems.find(i => i.itemId.toString() === quoteId.toString()),
  GET_DYNAMIC_GROUPS: state => state.dynamicColumns.map(g => ({
    groupName: g.groupName,
    groupId: g.groupId,
    colSpan: g.columns.length
  })),
  GET_DYNAMIC_COLUMNS: state => state.dynamicColumnHeader,
  GET_DYNAMIC_COLUMNS_ID_BY_TYPE: state => columnType => state.dynamicColumnHeader.find(i => i.columnType === columnType),
  GET_TOTAL_GLOBAL: state => {
    let totalCost = 0
    let totalCmed = 0
    let totalEstimate = 0
    let totalMinimum = 0
    let totalProposal = 0
    let dynamicTotal = {}

    state.dynamicColumnHeader
      .filter(c => c.columnType === 'total')
      .map(c => { dynamicTotal[c.columnId] = 0 })

    state.filteredList.map(i => {
      totalCost += i.quoted.priceTotal || 0
      totalCmed += i.quoted.cmedTotalPrice || 0
      totalEstimate += i.itemInfo.estimatedTotalPrice || 0
      totalMinimum += i.quoted.minimumTotalPrice || 0
      totalProposal += i.quoted.proposalTotalPrice || 0

      // sum dynamic totals
      if (i.quoted.priceTotal) {
        for (const k in dynamicTotal) {
          dynamicTotal[k] += i.dynamicColumns[k] && i.dynamicColumns[k].value
        }
      }
    })
    return {
      totalCost,
      totalCmed,
      totalEstimate,
      totalMinimum,
      totalProposal,
      dynamicTotal
    }
  }
}

const mutations = {
  SET_LIST_ITEMS: (state, { listItems }) => {
    state.listItems = listItems
  },
  SET_DYNAMIC_COLUMNS: (state, dynamicColumns) => {
    state.dynamicColumns = dynamicColumns
    state.dynamicColumnHeader = dynamicColumns.map(g => g.columns).flat()
  },
  FILTERED_LIST: (state, filteredList) => {
    state.filteredList = filteredList
  },
  SET_ITEM: (state, { itemId, info }) => {
    const newListItems = state.listItems.map(i => {
      if (i.itemId === itemId) {
        return {
          ...i,
          ...info
        }
      } else {
        return i
      }
    })
    state.listItems = newListItems
  },
  GROUP_FILTERS: (state) => {
    const groupedUnity = {}
    const groupedCostLab = {}
    const groupedMaxLab = {}
    const groupedDescription = {}
    const groupedNumber = {}

    for (const item of state.listItems) {
      if (!state.groupedFilter.number[item.itemInfo.number]) {
        groupedNumber[item.itemInfo.number] = {
          checked: false
        }
      }
      if (!state.groupedFilter.description[item.itemInfo.description]) {
        groupedDescription[item.itemInfo.description] = {
          checked: false
        }
      }
      if (!state.groupedFilter.unity[item.itemInfo.unity]) {
        groupedUnity[item.itemInfo.unity] = {
          checked: false
        }
      }

      // Add quoted lab
      if (item.quoted && item.quoted.lab && !state.groupedFilter.costLab[item.quoted.lab]) {
        groupedCostLab[item.quoted.lab] = {
          checked: false
        }
      }
      // Add associated lab
      if (item.associated && item.associated.lab && !state.groupedFilter.costLab[item.associated.lab]) {
        groupedCostLab[item.associated.lab] = {
          checked: false
        }
      }
      if (item.quoted && item.quoted.cmedLab && !state.groupedFilter.maxLab[item.quoted.cmedLab]) {
        groupedMaxLab[item.quoted.cmedLab] = {
          checked: false
        }
      }
    }
    state.groupedFilter.number = groupedNumber
    state.groupedFilter.description = groupedDescription
    state.groupedFilter.costLab = groupedCostLab
    state.groupedFilter.unity = groupedUnity
    state.groupedFilter.maxLab = groupedMaxLab
  },
  SET_ALL_SALES_VALUE: (state, payload) => {
    const newSale = state.listItems.map(i => {
      const data = { ...i }
      const payloadItem = payload.items.find(item => item.quote === i.itemId)
      if (payloadItem) {
        const lowerValue = DefineLowestMaxPrice(data.quoted.cmedPrice, data.itemInfo.estimatedPrice)

        data.quoted[`${payload.field}Price`] = payloadItem[`${payload.field}Price`]
        data.quoted[`${payload.field}Perc`] = payload.percentual
        data.quoted[`${payload.field}TotalPrice`] = payloadItem[`${payload.field}Price`] * i.itemInfo.quantity
        data.quoted[`${payload.field}IsHigherThanMaxprice`] = lowerValue < payloadItem[`${payload.field}Price`]
      }

      return data
    })
    state.listItems = newSale
  },
  UPDATE_DYNAMIC_UNIT_COLUMN: (state, { items }) => {
    const newListItems = JSON.parse(JSON.stringify(state.listItems))

    for (const item of items) {
      const listIndex = newListItems.findIndex(i => i.itemId.toString() === item.quoteId.toString())
      newListItems[listIndex].dynamicColumns[item.column].value = item.value
    }

    state.listItems = newListItems
  },
  UPDATE_DYNAMIC_LABS: (state, items) => {
    const newListItems = JSON.parse(JSON.stringify(state.listItems))

    for (const item of items) {
      const listIndex = newListItems.findIndex(i => i.itemId.toString() === item.quoteId.toString())
      newListItems[listIndex].dynamicColumns[item.column].value = item.value
    }

    state.listItems = newListItems
  },
  SET_DYNAMIC_UNIT: (state, { itemId, dynamicColumnId, value, columnTotal, total, perc, columnPerc }) => {
    const newListItems = state.listItems.map(i => {
      if (i.itemId === itemId) {
        return {
          ...i,
          dynamicColumns: {
            ...i.dynamicColumns,
            [dynamicColumnId]: {
              ...i.dynamicColumns[dynamicColumnId],
              value
            },
            [columnTotal]: {
              ...i.dynamicColumns[columnTotal],
              value: total
            },
            [columnPerc]: {
              ...i.dynamicColumns[columnPerc],
              value: perc
            }
          }
        }
      } else {
        return i
      }
    })
    state.listItems = newListItems
  },
  UPDATE_GROUP_TITLE: (state, { groupIdx, newTitle }) => {
    state.dynamicColumns[groupIdx].groupName = newTitle
  }
}

const actions = {
  LIST_ALL_ITEMS: ListAllItems,
  SELECT_QUOTE_ITEM: SelectQuoteItem,
  SAVE_SALE_MINIMUM: SaveSaleMinimum,
  UPDATE_SALE_LIST: UpdateSaleList,
  AUTO_SET_ASSOCIATED: AutoSetAssociated,
  ASSOCIATE_ITEM: AssociateItem,
  MINIMUM_RULE: ApplyMiniumRule,
  UPDATE_DYNAMIC_LIST: UpdateDynamicList,
  ADD_COLUMN: AddColumn,
  UPDATE_DYNAMIC_COLUMN: UpdateDynamicColumn,
  UPDATE_DYNAMIC_LABS: UpdateDynamicLabs,
  UPDATE_DYNAMIC_RANDOM_LABS: UpdateDynamicRandomLabs,
  UPDATE_DYNAMIC_GROUP_TITLE: UpdateDynamicGroupTitle,
  REMOVE_DYNAMIC_GROUP: RemoveDynamicGroup
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}



// WEBPACK FOOTER //
// ./src/view/item-pricing/state/index.js